import { useMemo } from 'react';
import { Navigate, type NavigateProps, useLocation } from 'react-router-dom';

import { mergeSearch, splitTo } from '@/utils/primitives';
import { DROPPED_QUERY_PARAMS_STATE_KEY } from '@/hooks/preservedNavigationConstants';

interface PreservedNavigateProps extends NavigateProps {
  /** Query parameter keys that should NOT be preserved from the current URL. */
  dropParams?: string[];
}

/**
 * A wrapper around React Router's `<Navigate>` (declarative redirect) that
 * preserves the current URL's query parameters (target-wins semantics).
 *
 * Use in route definitions where you would normally use `<Navigate to="..." replace />`.
 */
export const PreservedNavigate = ({ to, dropParams, state, ...rest }: PreservedNavigateProps) => {
  const location = useLocation();

  const resolvedTo = useMemo(() => {
    if (typeof to === 'string') {
      // Split without `new URL()` so relative targets like `"workflows"`
      // stay relative and resolve against the current nested route.
      const { path, search, hash } = splitTo(to);
      const mergedSearch = mergeSearch(location.search, search, dropParams);
      return `${path}${mergedSearch}${hash}`;
    }

    const mergedSearch = mergeSearch(location.search, to.search ?? '', dropParams);
    return {
      ...(to.pathname !== undefined && { pathname: to.pathname }),
      search: mergedSearch,
      ...(to.hash !== undefined && { hash: to.hash }),
    };
  }, [to, location.search, dropParams]);

  const mergedState = useMemo(() => {
    if (!dropParams || dropParams.length === 0) {
      return state;
    }
    if (state && typeof state === 'object') {
      return { ...state, [DROPPED_QUERY_PARAMS_STATE_KEY]: dropParams };
    }
    return { [DROPPED_QUERY_PARAMS_STATE_KEY]: dropParams, __originalState: state };
  }, [state, dropParams]);

  return <Navigate to={resolvedTo} state={mergedState} {...rest} />;
};
